// import hooks
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSave } from '../hooks/useSave';

// import types
import {
  EmployerCostSettingFlagForm,
  EmployerCostSettingsFormProps,
  EmployerCostSettingsFormValues,
  EmployerCostSettingsRequest
} from '../EmployerCostSettings.types';

// import utils
import { schema } from '../utils/EmployerCostSettings.schema';
import { convertFlagsObjectToArray } from 'utils/misc';
import { percentageToDecimalNumber } from '../utils/percentageToDecimal';

// import components
import { LoadingButton } from '@mui/lab';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import GeneralSection from './Sections/General/GeneralSection';
import HoursBalanceSection from './Sections/HoursBalance/HoursBalanceSection';
import SurchargesSection from './Sections/Surcharges/SurchargesSection';
import { percentageFields } from '../EmployerCostSettings.constants';

const EmployerCostSettingsForm = (props: EmployerCostSettingsFormProps) => {
  const {
    data: employerCostSettings,
    handleClose,
    isCopy,
    isNew = false
  } = props;
  const { t } = useTranslation('common');
  const { mutate: updateEmployerCostSettings, isLoading } =
    useSave(handleClose);

  const methods = useForm({
    defaultValues: schema.cast(employerCostSettings ?? {}),
    resolver: yupResolver(schema),
    mode: 'onChange'
  });

  const convertFormDataToRequest = (
    data: EmployerCostSettingsFormValues
  ): EmployerCostSettingsRequest => {
    const formData = { ...data };
    // Percentage fields are converted back to decimal fields
    percentageFields.forEach(
      (field: string) =>
        (formData[field] = percentageToDecimalNumber(
          methods.watch(field as any)
        ))
    );

    return {
      ...formData,
      flags: convertFlagsObjectToArray<EmployerCostSettingFlagForm>(data.flags)
    };
  };

  const onSubmit = (data) => {
    updateEmployerCostSettings({
      data: convertFormDataToRequest(data),
      isCopy: isCopy || isNew
    });
  };

  return (
    <MultiStepForm methods={methods} onSubmit={onSubmit}>
      <GeneralSection />
      <SurchargesSection />
      <HoursBalanceSection />
      <MultiStepForm.Actions>
        <LoadingButton
          loading={isLoading}
          type="submit"
          variant="contained"
          autoFocus
          color="primary"
        >
          {t('save')}
        </LoadingButton>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
};

export default EmployerCostSettingsForm;
